import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AsyncMultiSelectDropdown,
  DateInput,
  Dropdown,
  FormTemplate,
  IfFormData,
  Row6x6x,
  SubmitButton,
} from 'ui-builder';
import {
  AdditionalWorkflowDateOption,
  ProjectType,
  UpdateProjectDatesOption,
  usePlansService,
  useProjectPlanService,
} from 'nekst-api';
import { usePopupFormManager } from 'features/nekst-widgets';
import { useProjectContext } from '../../view/ProjectContext';

function WorkflowMultiDropdown(props: {
  projectType: ProjectType;
  setupAreaId: number;
}) {
  const plansService = usePlansService();

  return (
    <AsyncMultiSelectDropdown
      source="plansIds"
      label="Select Workflow(s)"
      loadOptionsFunc={async () => {
        const plans = await plansService.getAllTeamPlans(props.projectType, props.setupAreaId);
        return plans.map((plan) => ({
          value: plan.id,
          label: plan.name,
        }));
      }}
    />
  );
}

const DATE_OPTION_OPTIONS = [
  {
    value: AdditionalWorkflowDateOption.USE_TRANSACTION_DATES,
    label: 'Use Transaction Start and End Date',
  },
  {
    value: AdditionalWorkflowDateOption.CUSTOM_DATES,
    label: 'Set Custom Start and End Dates',
  },
  {
    value: AdditionalWorkflowDateOption.UPDATE_TRANSACTION_DATES,
    label: 'Update Transaction Dates',
  },
];

function DateOptionDropdown() {
  return (
    <Dropdown<AdditionalWorkflowDateOption>
      source="dateOption"
      label="How should task dates be calculated?"
      options={DATE_OPTION_OPTIONS}
    />
  );
}

const EXISTING_TASKS_DATES_OPTIONS = [
  {
    value: UpdateProjectDatesOption.DO_NOT_CHANGE_DATES,
    label: 'Do Not Change Existing Task Dates',
  },
  {
    value: UpdateProjectDatesOption.ADJUST_TASKS_DATES,
    label: 'Adjust Existing Task Dates',
  },
];

function ExistingTasksDatesOptionDropdown() {
  return (
    <Dropdown<UpdateProjectDatesOption>
      source="existingTasksDatesOption"
      label="How should existing task dates be handled?"
      options={EXISTING_TASKS_DATES_OPTIONS}
    />
  );
}

interface WorkflowSelectionFormData {
  plansIds: number[];
  dateOption: AdditionalWorkflowDateOption;
  customStartDate?: Date;
  customEndDate?: Date;
  existingTasksDatesOption: UpdateProjectDatesOption;
}

const needsCustomDates = (data: WorkflowSelectionFormData) =>
  data.dateOption === AdditionalWorkflowDateOption.CUSTOM_DATES
  || data.dateOption === AdditionalWorkflowDateOption.UPDATE_TRANSACTION_DATES;

const isUpdateTransactionDates = (data: WorkflowSelectionFormData) =>
  data.dateOption === AdditionalWorkflowDateOption.UPDATE_TRANSACTION_DATES;

export default function WorkflowSelectionModal(props: { projectId: number }) {
  const projectPlanService = useProjectPlanService();
  const popupFormManager = usePopupFormManager();
  const navigate = useNavigate();
  const projectContext = useProjectContext();

  const projectType = projectContext.data?.type;
  const setupAreaId = projectContext.data?._setupArea?.id;

  const submitFunc = async (data: WorkflowSelectionFormData) => {
    const result = await projectPlanService.addNewPlansToProject(
      props.projectId,
      data.plansIds,
      data.dateOption,
      needsCustomDates(data) ? data.customStartDate : undefined,
      needsCustomDates(data) ? data.customEndDate : undefined,
      isUpdateTransactionDates(data) ? data.existingTasksDatesOption : undefined,
    );

    popupFormManager.closePopup();
    navigate(`/transactions/${props.projectId}/plans/${result.id}/launch/keydates`);
  };

  if (!projectType || !setupAreaId) {
    return null;
  }

  return (
    <FormTemplate<WorkflowSelectionFormData>
      submitFormFunc={submitFunc}
      initialData={{
        plansIds: [],
        dateOption: AdditionalWorkflowDateOption.USE_TRANSACTION_DATES,
        existingTasksDatesOption: UpdateProjectDatesOption.DO_NOT_CHANGE_DATES,
      }}
      validationSchema={{
        plansIds: {
          type: 'string',
          constraints: {
            required: true,
          },
        },
      }}
      title="Add Workflow"
      buttons={<SubmitButton text="Continue" />}
    >
      <WorkflowMultiDropdown
        projectType={projectType}
        setupAreaId={setupAreaId}
      />
      <DateOptionDropdown />
      <IfFormData<WorkflowSelectionFormData> conditionFunc={needsCustomDates}>
        <Row6x6x>
          <DateInput source="customStartDate" label="Start Date" />
          <DateInput source="customEndDate" label="End Date" />
        </Row6x6x>
      </IfFormData>
      <IfFormData<WorkflowSelectionFormData> conditionFunc={isUpdateTransactionDates}>
        <ExistingTasksDatesOptionDropdown />
      </IfFormData>
    </FormTemplate>
  );
}
